import { Day } from '@app/api/models/openingTimes.models';
import { Input, Directive, Output, EventEmitter } from '@angular/core';

import { FormGroup } from '@angular/forms';

import {
  CountriesAndCategories,
  Countries,
  ParsedPhoneNumber,
  ICategory,
  EstablishmentCategory,
  OfferingsSubCategory,
  ICategoryOfferingsMap,
  ISubCategory,
  BusinessData,
} from '@app/api/models/api-models';
import { IOptionGroup } from '../../api/models/offerings';
import { countryPaymentMethods } from '../../api/models/countryPaymentMethods';

import { PhoneNumberValidator, PhoneNumberPattern, zipCodeExpressions, defaultZipCodeExpression } from '../validations';
import { PhoneNumber, NationalNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

@Directive()
export abstract class BaseComponent {
  @Output() categoryOfferingsEmitter = new EventEmitter<IOptionGroup[]>();
  @Output() offeringsSerchEmitter = new EventEmitter<string>();
  @Input() profileData: BusinessData;
  @Input() categoriesAndCountries: CountriesAndCategories;
  @Input() establishmentCategory: string;
  @Input() countryCode: string;
  firstFormGroup: FormGroup;
  zipCodeRegex: RegExp = defaultZipCodeExpression;
  countryMismatchError = false;

  protected establishmentCountryCode: string;
  private acceptedAddressState: {
    firstForm: Pick<Record<string, unknown>, 'address' | 'postal' | 'city' | 'country' | 'phone' | 'area'>;
    website?: unknown;
  } | null = null;

  availableOfferings: IOptionGroup[];
  availableServices: IOptionGroup[];
  availablePaymentMethods: IOptionGroup[];

  selectedOffering: string[] = [];
  selectedServices: string[] = [];
  selectedPayments: string[] = [];

  omitSpecialChar(event: any) {
    const k: number = event.charCode;
    return (k > 64 && k < 91) || (k > 96 && k < 123) || k === 8 || k === 32 || (k >= 48 && k <= 57);
  }

  updatePhoneValidation(phoneNumberCode: string) {
    this.firstFormGroup.get('phone').setValidators([...PhoneNumberPattern, PhoneNumberValidator(phoneNumberCode)]);
    this.firstFormGroup.get('phone').updateValueAndValidity();
  }

  setPhoneCountryCode(event: any) {
    this.updatePhoneValidation(event.value);
  }

  /**
   * When users changes country, a new area code needs to be set.
   * @param event country selected
   */
  changeCountry(event: any) {
    this.setZipCodeRegex(event.value);
    this.setAreaCode(event.value);
    this.setCountryCode(event.value);
  }

  /**
   * Sets the ZipCodeRegex depending on the country selected
   * @param countryCode Country Code
   */
  setZipCodeRegex(countryCode: string): void {
    this.zipCodeRegex = this.getCountryZipCodeRegex(countryCode);
  }

  /**
   * Set new areacode for selected country.
   * @param countryName the country name
   */
  setAreaCode(countryName: string) {
    if (!countryName) {
      return;
    }

    const newAreaValue = this.categoriesAndCountries.countries.find(c => c.name === countryName).code;
    this.firstFormGroup.get('area').setValue(newAreaValue);

    this.updatePhoneValidation(newAreaValue);
  }

  setCountryCode(countryName: string) {
    if (!countryName) {
      return;
    }

    this.countryCode = this.categoriesAndCountries.countries.find(c => c.name === countryName).countryCode;
    return this.countryCode;
  }

  protected hasForeignCountrySelection(): boolean {
    return this.isCountryMismatch(this.firstFormGroup?.get('country')?.value);
  }

  protected isCountryMismatch(countryName: string): boolean {
    if (!countryName || !this.establishmentCountryCode) {
      return false;
    }

    return this.getCountryCodeByName(countryName) !== this.establishmentCountryCode;
  }

  protected clearCountryMismatchError(): void {
    this.countryMismatchError = false;

    const addressControl = this.firstFormGroup?.get('address');
    if (addressControl?.hasError('countryMismatch')) {
      addressControl.setErrors(null);
      addressControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  protected restoreAcceptedAddressState(secondaryFormGroup?: FormGroup): void {
    if (!this.acceptedAddressState) {
      return;
    }

    this.firstFormGroup.patchValue(this.acceptedAddressState.firstForm);
    if (secondaryFormGroup?.get('website')) {
      secondaryFormGroup.get('website').setValue(this.acceptedAddressState.website);
    }
  }

  protected storeAcceptedAddressState(secondaryFormGroup?: FormGroup): void {
    if (!this.firstFormGroup) {
      return;
    }

    const addressState = this.firstFormGroup.getRawValue();
    this.acceptedAddressState = {
      firstForm: {
        address: addressState.address,
        postal: addressState.postal,
        city: addressState.city,
        country: addressState.country,
        phone: addressState.phone,
        area: addressState.area,
      },
      website: secondaryFormGroup?.get('website')?.value ?? '',
    };
  }

  private getCountryCodeByName(countryName: string): string | undefined {
    return this.categoriesAndCountries?.countries.find(c => c.name === countryName)?.countryCode;
  }

  parsePhoneNumber(selectedCountry: Countries, phoneToParse: string): ParsedPhoneNumber {
    let phoneNumberCountryCode = selectedCountry.code;
    let phoneNumber: NationalNumber;

    const phoneNumberFromHydra: PhoneNumber | undefined = phoneToParse && parsePhoneNumberFromString(phoneToParse);

    if (phoneNumberFromHydra) {
      phoneNumberCountryCode = `+${phoneNumberFromHydra.countryCallingCode}`;
      phoneNumber = phoneNumberFromHydra.nationalNumber;
    }

    return { phoneNumber, phoneNumberCountryCode };
  }

  /**
   * Compares the original opening hours with the changes done by the user
   * @param newOpeningHours new OpeningHours
   */
  openingHoursChanged(newOpeningHours): boolean {
    const { openingTimes } = this.profileData;
    const openingHoursWihoutEmpty = Object.keys(newOpeningHours).filter((key: string) => newOpeningHours[key].length);

    // Compares if the length is the same
    if (Object.keys(openingTimes).length !== openingHoursWihoutEmpty.length) {
      return true;
    }

    // Compares if the content of each opening hour is the same
    for (const openingDay of Object.keys(openingTimes)) {
      const originalOpeningHours: Day[] = openingTimes[openingDay];
      const toCompareOpeningHours: Day[] = newOpeningHours[openingDay];

      if (toCompareOpeningHours && JSON.stringify(originalOpeningHours) !== JSON.stringify(toCompareOpeningHours)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Compares if Objects are different
   * @param x Object 1
   * @param y Object 2
   */
  objectsAreDiff(x, y) {
    if (x.length !== y.length) {
      return true;
    } else {
      const l = x.map((elem, index) => {
        Object.keys(y[index]).forEach(key => y[index][key] === '' && delete y[index][key]);
        Object.keys(elem).forEach(key => elem[key] === null && delete elem[key]);

        const dif = Object.keys(elem).find(key => elem[key] !== y[index][key]);
        return dif ? true : false;
      });
      return l.some(r => r === true);
    }
  }

  /**
   * Receives a Countries list and returns a list of ordered phone number codes without duplicate values
   * @param countries List of Countries
   */
  sortPhoneNumberCountryCodes(countries: Countries[]): string[] {
    let phoneNumberCodes: string[] = [];
    phoneNumberCodes = countries.map(country => {
      return country.code;
    });
    phoneNumberCodes.sort((code1: string, code2: string) => this.sortPhoneNumberCode(code1, code2));
    return Array.from(new Set(phoneNumberCodes));
  }

  private sortPhoneNumberCode(code1: string, code2: string): number {
    const firstValue = Number(code1.replace(/\s/g, ''));
    const secondValue = Number(code2.replace(/\s/g, ''));
    return firstValue - secondValue;
  }

  /**
   * Gets the available payment methods for a country
   * @param countryCode Country Code
   * @param availablePayments Available Payments
   */
  getCountryAvailablePaymentMethods(countryCode: string, defaultPaymentsArray: string[]): ICategory[] {
    let defaultPayments = [];
    let countryPayments = [];
    const availablePaymentsInCountry = countryPaymentMethods[countryCode];

    defaultPayments = defaultPaymentsArray.map(x => {
      return { name: x, selected: false };
    });

    if (availablePaymentsInCountry) {
      countryPayments = availablePaymentsInCountry.map((payment: string) => {
        return { name: payment, selected: false };
      });
    }

    return countryPayments.length ? countryPayments : defaultPayments;
  }

  getCountryZipCodeRegex(countryCode: string): RegExp {
    return countryCode in zipCodeExpressions ? zipCodeExpressions[countryCode] : defaultZipCodeExpression;
  }

  /**
   * Normalizes the description value by collapsing multiple consecutive spaces
   * @param text Text to normalize
   * @param shouldTrim Whether to trim leading/trailing spaces
   */
  private normalizeDescription(text: string, shouldTrim = false): string {
    let normalized = text.replace(/\s{2,}/g, ' ');
    if (shouldTrim) {
      normalized = normalized.trim();
    }
    return normalized;
  }

  /**
   * Normalizes the description value after paste by trimming leading/trailing spaces and collapsing multiple spaces
   */
  onDescriptionPaste(event: ClipboardEvent, formGroup: FormGroup): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const normalizedText = this.normalizeDescription(pastedText, true);
    const descriptionControl = formGroup.get('description');
    const currentValue = (descriptionControl?.value ?? '') as string;
    const textArea = event.target as HTMLTextAreaElement | null;

    if (!textArea) {
      descriptionControl?.setValue(this.normalizeDescription(currentValue + normalizedText, false));
      return;
    }

    const selectionStart = textArea.selectionStart ?? currentValue.length;
    const selectionEnd = textArea.selectionEnd ?? currentValue.length;
    const updatedValue = currentValue.slice(0, selectionStart) + normalizedText + currentValue.slice(selectionEnd);

    descriptionControl?.setValue(updatedValue);

    // Restore caret after programmatic update so consecutive paste/type behaves naturally.
    const nextCaretPosition = selectionStart + normalizedText.length;
    setTimeout(() => textArea.setSelectionRange(nextCaretPosition, nextCaretPosition));
  }

  /**
   * Normalizes the description value on input events by collapsing multiple consecutive spaces.
   * Doesn't trim during typing to allow entering spaces between words.
   */
  onDescriptionInput(formGroup: FormGroup): void {
    const descriptionControl = formGroup.get('description');
    if (descriptionControl?.value) {
      const normalizedText = this.normalizeDescription(descriptionControl.value, false);
      if (normalizedText !== descriptionControl.value) {
        descriptionControl.setValue(normalizedText, { emitEvent: false });
      }
    }
  }

  /**
   * Normalizes the description value on blur by trimming leading/trailing spaces
   */
  onDescriptionBlur(formGroup: FormGroup): void {
    const descriptionControl = formGroup.get('description');
    if (descriptionControl?.value) {
      const normalizedText = this.normalizeDescription(descriptionControl.value, true);
      if (normalizedText !== descriptionControl.value) {
        descriptionControl.setValue(normalizedText);
      }
    }
  }

  validateZipCodeOnChange(): void {
    const zipCode: string = this.firstFormGroup.controls.postal.value;
    if (zipCode && this.zipCodeRegex.test(zipCode)) {
      this.firstFormGroup.controls.postal.setErrors(null);
    } else {
      this.firstFormGroup.controls.postal.setErrors({ incorrect: true });
    }
  }

  /**
   * Retrieves the Offerings SubCategories for a specific establishment category and the offerings of each SubCategory.
   * Then it maps the offerings to an array of IOptionGroup, the interface used in the generic Offerings/Services component
   * @param establishmentCategory Establishment Category
   */
  getAvailableOfferingsForSpecificEstablishment(establishmentCategory: EstablishmentCategory): IOptionGroup[] {
    const { categories, offeringsSubCategories } = this.categoriesAndCountries;
    const subCategories: OfferingsSubCategory[] = categories.find(
      (categoryOfferingMap: ICategoryOfferingsMap) => categoryOfferingMap.category.toLowerCase() === establishmentCategory
    )?.subCategories;
    const reOrderedSubCategories = this.reOrderSubCategories(offeringsSubCategories, subCategories);
    const availableOfferings = reOrderedSubCategories.map((offeringSubCategory: OfferingsSubCategory) => {
      const offeringsArray: ISubCategory = offeringsSubCategories[offeringSubCategory];
      const { subCategoryId, offerings } = offeringsArray;
      return {
        groupTitle: subCategoryId,
        options: offerings.map((offering: string) => ({ name: offering, selected: false })),
        isOpen: false,
      } as IOptionGroup;
    });
    return availableOfferings;
  }

  /**
   * ReOrders the array of subcategories retrieved from the user
   * @param correctOrder Correct Order to Follow
   * @param subCategories SubCategories to order
   */
  reOrderSubCategories(correctOrder, subCategories): OfferingsSubCategory[] {
    const orderSubCategories = [];
    Object.keys(correctOrder).forEach((key: OfferingsSubCategory) => {
      const keyIndex = subCategories.indexOf(key);
      if (keyIndex != -1) {
        orderSubCategories.push(subCategories[keyIndex]);
      }
    });
    return orderSubCategories;
  }

  /**
   * Sets the availableOfferings to feed the generic Services component
   */
  setAvailableOfferings(): void {
    const establishmentCategory: EstablishmentCategory = this.establishmentCategory.toLocaleLowerCase() as EstablishmentCategory;
    this.availableOfferings = this.getAvailableOfferingsForSpecificEstablishment(establishmentCategory);
  }

  /**
   * Sets the availableServices variable to feed the generic Offerings component
   * @param services Services list
   */
  setAvailableServices(services): void {
    const servicesArray = services.map(service => {
      return { name: service, selected: false };
    });
    this.availableServices = [{ options: servicesArray }];
  }

  /**
   * Checks if the profileData and categoriesAndCountries are available and sets the paymentsArray depending on the countryCode
   * @param changes SimpleChanges object
   */
  setAvailablePaymentMethods(): void {
    if (this.categoriesAndCountries && this.countryCode) {
      const { payments } = this.categoriesAndCountries;
      this.availablePaymentMethods = [{ options: this.getCountryAvailablePaymentMethods(this.countryCode, payments) }];
    }
  }

  handleOfferingsSearch(search: string): void {
    this.offeringsSerchEmitter.emit(search);
  }
}
